import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 音乐乐理 - 横屏「五线谱动画」风格
 *
 * 核心亮点：
 * - SVG 五线谱绘制 + 音符逐个弹入
 * - 钢琴键盘底部动画（对应音符高亮）
 * - 频率波形可视化
 * - 和弦构成可视化
 * - 节拍指示器（节拍器摆动）
 *
 * 尺寸：1920x1080 横屏
 */

interface MusicNote {
  pitch: string;      // e.g. 'C4', 'E4', 'G4'
  name: string;       // e.g. 'do', 'mi', 'sol'
  duration: 'whole' | 'half' | 'quarter' | 'eighth';
  staffPosition: number; // 0 = middle line, positive = up, negative = down
  pianoKey: number;   // 0-based piano key index from left
}

interface MusicTheoryTemplateProps {
  topic: string;
  subtitle: string;
  clef: 'treble' | 'bass';
  timeSignature: string;
  notes: MusicNote[];
  chordName: string;
  chordNotes: string[];
  concept: string;
  explanation: string[];
  bpm: number;
}

const DEFAULT_PROPS: MusicTheoryTemplateProps = {
  topic: 'C 大三和弦构成',
  subtitle: 'C Major Triad: C - E - G',
  clef: 'treble',
  timeSignature: '4/4',
  notes: [
    { pitch: 'C4', name: 'Do', duration: 'half', staffPosition: -2, pianoKey: 0 },
    { pitch: 'E4', name: 'Mi', duration: 'half', staffPosition: 0, pianoKey: 4 },
    { pitch: 'G4', name: 'Sol', duration: 'half', staffPosition: 1, pianoKey: 7 },
    { pitch: 'C5', name: 'Do', duration: 'whole', staffPosition: 3, pianoKey: 12 },
  ],
  chordName: 'C Major',
  chordNotes: ['C', 'E', 'G'],
  concept: '大三和弦 = 根音 + 大三度 + 小三度',
  explanation: [
    'C → E：大三度（4 个半音）',
    'E → G：小三度（3 个半音）',
    '大三和弦听起来明亮、稳定',
    '是西方音乐最基本的和弦类型',
  ],
  bpm: 80,
};

const PIANO_WHITE_KEYS = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];
const PIANO_BLACK_MAP: Record<number, boolean> = { 1: true, 3: true, 6: true, 8: true, 10: true };

export const MusicTheoryTemplate: React.FC<Partial<MusicTheoryTemplateProps>> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Staff area
  const staffX = 120;
  const staffY = 200;
  const staffW = 1050;
  const staffLineSpacing = 14;
  const staffH = staffLineSpacing * 4;

  // Phases
  const headerProgress = spring({ frame, fps, config: { damping: 18, stiffness: 90 } });
  const staffDraw = interpolate(frame, [25, 65], [0, 1], { extrapolateRight: 'clamp' });

  // Notes
  const noteStart = 80;
  const noteInterval = 50;

  // Active note for piano highlight
  const activeNoteIdx = Math.min(
    Math.floor(interpolate(frame, [noteStart, noteStart + p.notes.length * noteInterval], [0, p.notes.length], { extrapolateRight: 'clamp' })),
    p.notes.length - 1
  );

  // Concept & explanation
  const conceptStart = noteStart + p.notes.length * noteInterval + 20;
  const conceptOpacity = interpolate(frame, [conceptStart, conceptStart + 20], [0, 1], { extrapolateRight: 'clamp' });

  const expStart = conceptStart + 30;

  // Metronome
  const beatDuration = (60 / p.bpm) * fps;
  const metronomeAngle = interpolate(Math.sin((frame / beatDuration) * Math.PI), [-1, 1], [-25, 25]);

  // Wave frequency visualization
  const waveStart = noteStart + 20;
  const waveOpacity = interpolate(frame, [waveStart, waveStart + 20], [0, 1], { extrapolateRight: 'clamp' });

  // Piano key ranges
  const totalWhiteKeys = 15;
  const pianoKeyW = 40;
  const pianoX = 120;
  const pianoY = 770;
  const pianoKeyH = 160;
  const blackKeyH = 100;

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(180deg, #121218 0%, #1a1a28 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Inter", sans-serif',
        color: 'white',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          position: 'absolute',
          top: 36,
          left: 48,
          opacity: headerProgress,
          transform: `translateY(${(1 - headerProgress) * -12}px)`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <div style={{ padding: '4px 14px', background: '#6366f118', border: '1px solid #6366f140', borderRadius: 8, fontSize: 22, color: '#818cf8', fontWeight: 600 }}>
            🎵 MUSIC THEORY
          </div>
          <div style={{ padding: '4px 10px', background: 'rgba(255,255,255,0.04)', borderRadius: 6, fontSize: 22, color: 'rgba(255,255,255,0.3)' }}>
            ♩ = {p.bpm} BPM
          </div>
        </div>
        <h1 style={{ fontSize: 40, fontWeight: 800, margin: 0 }}>{p.topic}</h1>
        <p style={{ fontSize: 26, color: 'rgba(255,255,255,0.35)', marginTop: 4, fontStyle: 'italic' }}>{p.subtitle}</p>
      </div>

      {/* ========== STAFF (五线谱) ========== */}
      <svg
        style={{ position: 'absolute', left: 0, top: 120 }}
        width="1920"
        height="500"
        viewBox="0 0 1920 500"
      >
        {/* Five lines */}
        {[0, 1, 2, 3, 4].map((i) => (
          <line
            key={i}
            x1={staffX}
            y1={staffY + i * staffLineSpacing}
            x2={staffX + staffW * staffDraw}
            y2={staffY + i * staffLineSpacing}
            stroke="rgba(255,255,255,0.2)"
            strokeWidth="1.5"
          />
        ))}

        {/* Treble clef symbol */}
        {staffDraw > 0.1 && (
          <text
            x={staffX + 10}
            y={staffY + staffH - 2}
            fill="rgba(255,255,255,0.4)"
            fontSize="60"
            fontFamily="serif"
            opacity={staffDraw}
          >
            𝄞
          </text>
        )}

        {/* Time signature */}
        {staffDraw > 0.3 && (
          <text
            x={staffX + 70}
            y={staffY + staffH / 2 + 10}
            fill="rgba(255,255,255,0.35)"
            fontSize="26"
            fontWeight="bold"
            textAnchor="middle"
            opacity={staffDraw}
          >
            {p.timeSignature}
          </text>
        )}

        {/* Notes on staff */}
        {p.notes.map((note, i) => {
          const start = noteStart + i * noteInterval;
          const noteScale = spring({
            frame: frame - start,
            fps,
            from: 0,
            to: 1,
            config: { damping: 10, stiffness: 200, mass: 0.4 },
          });
          if (noteScale < 0.01) return null;

          const nx = staffX + 150 + i * (staffW - 200) / (p.notes.length);
          const middleLine = staffY + 2 * staffLineSpacing;
          const ny = middleLine - note.staffPosition * (staffLineSpacing / 2);

          const isActive = i === activeNoteIdx && frame >= start;
          const bounce = isActive ? interpolate(Math.sin((frame - start) / 6), [-1, 1], [-3, 3]) : 0;

          return (
            <g key={i} opacity={noteScale} transform={`translate(0, ${bounce})`}>
              {/* Ledger lines if needed */}
              {note.staffPosition < -2 && (
                <line
                  x1={nx - 16}
                  y1={staffY + staffH + staffLineSpacing}
                  x2={nx + 16}
                  y2={staffY + staffH + staffLineSpacing}
                  stroke="rgba(255,255,255,0.2)"
                  strokeWidth="1.5"
                />
              )}
              {note.staffPosition > 4 && (
                <line
                  x1={nx - 16}
                  y1={staffY - staffLineSpacing}
                  x2={nx + 16}
                  y2={staffY - staffLineSpacing}
                  stroke="rgba(255,255,255,0.2)"
                  strokeWidth="1.5"
                />
              )}

              {/* Note head (ellipse) */}
              <ellipse
                cx={nx}
                cy={ny}
                rx={9}
                ry={7}
                fill={isActive ? '#818cf8' : 'white'}
                transform={`rotate(-15, ${nx}, ${ny})`}
              />
              {/* Glow for active */}
              {isActive && (
                <ellipse
                  cx={nx}
                  cy={ny}
                  rx={18}
                  ry={14}
                  fill="#818cf8"
                  opacity="0.15"
                  transform={`rotate(-15, ${nx}, ${ny})`}
                />
              )}
              {/* Stem */}
              {note.duration !== 'whole' && (
                <line
                  x1={nx + 8}
                  y1={ny}
                  x2={nx + 8}
                  y2={ny - 40}
                  stroke={isActive ? '#818cf8' : 'white'}
                  strokeWidth="2"
                />
              )}

              {/* Note name label */}
              <text
                x={nx}
                y={ny + 40}
                fill={isActive ? '#818cf8' : 'rgba(255,255,255,0.5)'}
                fontSize="22"
                fontWeight="bold"
                textAnchor="middle"
              >
                {note.name}
              </text>
              <text
                x={nx}
                y={ny + 70}
                fill="rgba(255,255,255,0.25)"
                fontSize="22"
                textAnchor="middle"
              >
                {note.pitch}
              </text>

              {/* Interval arc between notes */}
              {i > 0 && (
                <path
                  d={`M${staffX + 150 + (i - 1) * (staffW - 200) / p.notes.length + 12},${ny - 50} Q${(staffX + 150 + (i - 1) * (staffW - 200) / p.notes.length + nx) / 2},${ny - 75} ${nx - 12},${ny - 50}`}
                  fill="none"
                  stroke="#818cf880"
                  strokeWidth="1.5"
                  strokeDasharray="4 3"
                />
              )}
            </g>
          );
        })}
      </svg>

      {/* ========== FREQUENCY WAVE VIZ ========== */}
      <div
        style={{
          position: 'absolute',
          top: 520,
          left: staffX,
          opacity: waveOpacity,
        }}
      >
        <div style={{ fontSize: 26, color: 'rgba(255,255,255,0.25)', marginBottom: 8 }}>声波频率可视化</div>
        <svg width={staffW} height={120} viewBox={`0 0 ${staffW} 120`}>
          {p.notes.slice(0, activeNoteIdx + 1).map((note, i) => {
            const freq = 1 + note.staffPosition * 0.3;
            const amplitude = 30 - i * 3;
            const color = i === activeNoteIdx ? '#818cf8' : `rgba(129,140,248,${0.15 + i * 0.05})`;
            const points: string[] = [];
            for (let x = 0; x < staffW; x += 3) {
              const y = 60 + amplitude * Math.sin(x * freq * 0.03 + frame * 0.08 + i * 2);
              points.push(`${x},${y}`);
            }
            return (
              <polyline key={i} points={points.join(' ')} fill="none" stroke={color} strokeWidth="2" opacity="0.6" />
            );
          })}
        </svg>
      </div>

      {/* ========== CONCEPT & EXPLANATION (right panel) ========== */}
      <div
        style={{
          position: 'absolute',
          top: 180,
          right: 60,
          width: 600,
        }}
      >
        {/* Chord visualization */}
        <div
          style={{
            padding: '20px',
            background: '#818cf80a',
            borderRadius: 16,
            border: '1px solid #818cf820',
            textAlign: 'center',
            marginBottom: 40,
            opacity: conceptOpacity,
          }}
        >
          <div style={{ fontSize: 24, color: '#818cf8', fontWeight: 600, marginBottom: 10, letterSpacing: '0.1em' }}>和弦构成</div>
          <div style={{ fontSize: 42, fontWeight: 900, letterSpacing: '0.2em' }}>
            {p.chordNotes.map((n, i) => (
              <React.Fragment key={i}>
                {i > 0 && <span style={{ color: 'rgba(255,255,255,0.2)', margin: '0 8px' }}>·</span>}
                <span style={{ color: '#818cf8' }}>{n}</span>
              </React.Fragment>
            ))}
          </div>
          <div style={{ fontSize: 24, color: 'rgba(255,255,255,0.5)', marginTop: 8 }}>{p.chordName}</div>
        </div>

        {/* Concept */}
        <div
          style={{
            padding: '16px 20px',
            background: 'rgba(255,255,255,0.03)',
            borderRadius: 12,
            border: '1px solid rgba(255,255,255,0.06)',
            marginBottom: 36,
            opacity: conceptOpacity,
          }}
        >
          <div style={{ fontSize: 24, fontWeight: 700, color: '#fbbf24' }}>🎯 {p.concept}</div>
        </div>

        {/* Explanation lines */}
        {p.explanation.map((line, i) => {
          const start = expStart + i * 30;
          const opacity = interpolate(frame, [start, start + 20], [0, 1], { extrapolateRight: 'clamp' });
          const x = interpolate(frame, [start, start + 20], [15, 0], { extrapolateRight: 'clamp' });
          return (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                marginBottom: 12,
                opacity,
                transform: `translateX(${x}px)`,
              }}
            >
              <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#818cf8', opacity: 0.5, flexShrink: 0 }} />
              <span style={{ fontSize: 26, color: 'rgba(255,255,255,0.7)', lineHeight: 1.5 }}>{line}</span>
            </div>
          );
        })}

        {/* Metronome */}
        <div
          style={{
            marginTop: 24,
            display: 'flex',
            justifyContent: 'center',
            opacity: interpolate(frame, [40, 60], [0, 0.5], { extrapolateRight: 'clamp' }),
          }}
        >
          <div
            style={{
              width: 4,
              height: 60,
              background: 'rgba(255,255,255,0.2)',
              borderRadius: 2,
              transformOrigin: 'bottom center',
              transform: `rotate(${metronomeAngle}deg)`,
              position: 'relative',
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 12,
                height: 12,
                borderRadius: '50%',
                background: '#818cf8',
              }}
            />
          </div>
        </div>
      </div>

      {/* ========== PIANO KEYBOARD ========== */}
      <div
        style={{
          position: 'absolute',
          left: pianoX,
          top: pianoY,
          width: totalWhiteKeys * pianoKeyW,
          height: pianoKeyH,
        }}
      >
        {/* White keys */}
        {Array.from({ length: totalWhiteKeys }).map((_, i) => {
          const isActive = p.notes.some(
            (n, ni) => n.pianoKey === i && ni <= activeNoteIdx && frame >= noteStart + ni * noteInterval
          );
          return (
            <div
              key={`w${i}`}
              style={{
                position: 'absolute',
                left: i * pianoKeyW,
                top: 0,
                width: pianoKeyW - 2,
                height: pianoKeyH,
                background: isActive
                  ? 'linear-gradient(180deg, #c7d2fe, #818cf8)'
                  : 'linear-gradient(180deg, #f5f5f5, #d4d4d4)',
                borderRadius: '0 0 6px 6px',
                border: '1px solid rgba(0,0,0,0.15)',
                boxShadow: isActive
                  ? '0 4px 15px rgba(129,140,248,0.3)'
                  : '0 2px 4px rgba(0,0,0,0.1)',
                transition: 'background 0.1s',
              }}
            >
              {/* Key label at bottom */}
              <div
                style={{
                  position: 'absolute',
                  bottom: 8,
                  left: 0,
                  right: 0,
                  textAlign: 'center',
                  fontSize: 11,
                  fontWeight: 600,
                  color: isActive ? '#4338ca' : '#999',
                }}
              >
                {PIANO_WHITE_KEYS[i % 7]}{Math.floor(i / 7) + 4}
              </div>
            </div>
          );
        })}

        {/* Black keys */}
        {Array.from({ length: totalWhiteKeys }).map((_, i) => {
          const keyInOctave = i % 7;
          const hasBlack = keyInOctave !== 2 && keyInOctave !== 6;
          if (!hasBlack || i >= totalWhiteKeys - 1) return null;

          return (
            <div
              key={`b${i}`}
              style={{
                position: 'absolute',
                left: i * pianoKeyW + pianoKeyW * 0.65,
                top: 0,
                width: pianoKeyW * 0.6,
                height: blackKeyH,
                background: '#1a1a2e',
                borderRadius: '0 0 4px 4px',
                zIndex: 2,
                boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
              }}
            />
          );
        })}
      </div>

      {/* Progress */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 480], [0, 100], { extrapolateRight: 'clamp' })}%`,
          height: 3,
          background: '#818cf8',
        }}
      />
    </AbsoluteFill>
  );
};
